import { useState, useRef, useEffect } from "react";
import * as XLSX from "xlsx";

const SUPABASE_URL  = "https://jsyzxscinfrfcebiqmja.supabase.co";
const SUPABASE_KEY  = "sb_publishable_KtY55FFvhf5WfSZpO3nSoA_02XnFl1l";
const ANTHROPIC_KEY = process.env.REACT_APP_ANTHROPIC_KEY || "";
import { supabase } from "./supabaseClient";

// ─── CSV Parser ───────────────────────────────────────────────────────────────
const BL_MAP = {
  name:    ["Owner Name","owner_name","First Name","full_name","Name"],
  phone:   ["Phone","phone","Mobile Phone","Cell Phone","phone_number"],
  address: ["Property Address","address","Street Address","property_address"],
  city:    ["City","city","Mailing City"],
  state:   ["State","state","Mailing State"],
  zip:     ["Zip","zip","Zip Code","Mailing Zip"],
  sqft:    ["Sqft","sqft","Square Feet","Living Sqft","building_sqft"],
  equity:  ["Equity %","equity_percent","Equity Percent","Estimated Equity %"],
  taxLien: ["Tax Lien","tax_lien","Tax Delinquent","tax_delinquent"],
  vacant:  ["Vacant","vacant","Vacancy","Occupancy Status"],
};

function findCol(h, keys) {
  for (const k of keys) { const f = h.find(x => x.trim().toLowerCase() === k.toLowerCase()); if (f) return f; }
  return null;
}

function parseCSV(text) {
  const lines = text.trim().split("\n");
  if (lines.length < 2) return [];
  const headers = lines[0].split(",").map(h => h.replace(/"/g,"").trim());
  const cols = {};
  for (const [f,keys] of Object.entries(BL_MAP)) cols[f] = findCol(headers, keys);
  const contacts = [];
  for (let i = 1; i < lines.length; i++) {
    const vals = lines[i].split(",").map(v => v.replace(/"/g,"").trim());
    const row = {}; headers.forEach((h,idx) => { row[h] = vals[idx]||""; });
    const name = cols.name ? row[cols.name] : "";
    const phone = cols.phone ? row[cols.phone] : "";
    if (!name && !phone) continue;
    const addr = [cols.address?row[cols.address]:"",cols.city?row[cols.city]:"",cols.state?row[cols.state]:"",cols.zip?row[cols.zip]:""].filter(Boolean).join(", ");
    const taxRaw = cols.taxLien?row[cols.taxLien]:"";
    const vacRaw = cols.vacant?row[cols.vacant]:"";
    contacts.push({
      id: Date.now()+i, name, phone, address:addr||"",
      sqft: cols.sqft?parseInt(row[cols.sqft])||null:null,
      equity: cols.equity?parseInt(row[cols.equity])||0:0,
      taxLien: /yes|true|1|y/i.test(taxRaw),
      vacant:  /yes|true|1|y|vacant/i.test(vacRaw),
      status:"pending", dncStatus:"unchecked",
      reply:null, classification:null, personalizedMsg:null,
      drip:null, dripScheduledDate:null,
    });
  }
  return contacts;
}

// ─── AI helpers ───────────────────────────────────────────────────────────────
async function callClaude(system, user) {
  const res = await fetch("https://api.anthropic.com/v1/messages", {
    method:"POST",
    headers:{"Content-Type":"application/json","x-api-key":ANTHROPIC_KEY,"anthropic-version":"2023-06-01","anthropic-dangerous-direct-browser-access":"true"},
    body:JSON.stringify({model:"claude-sonnet-5-5",max_tokens:300,system,messages:[{role:"user",content:user}]}),
  });
  const data = await res.json();
  return data.content?.find(b=>b.type==="text")?.text?.trim()||"";
}

async function personalizeMessage(contact, template) {
  return callClaude(
    `You are a real estate wholesaler texting motivated sellers. Personalize this SMS template. Keep under 160 chars. Natural, not salesy. Output ONLY the message text.`,
    `Template: "${template}"\nContact: ${contact.name} | Property: ${contact.address} | Equity: ${contact.equity}% | Tax Lien: ${contact.taxLien} | Vacant: ${contact.vacant}`
  );
}

async function classifyReply(reply, contactName) {
  const result = await callClaude(
    `You classify seller replies for a real estate wholesaler. Return ONLY valid JSON.`,
    `Seller "${contactName}" replied: "${reply}"\nReturn JSON: {"classification":"motivated|info|not_interested|opted_out","score":0-100,"hot":true/false,"summary":"one sentence","suggestedReply":"under 160 chars"}`
  );
  try { return JSON.parse(result.replace(/```json|```/g,"").trim()); }
  catch { return {classification:"info",score:50,hot:false,summary:reply,suggestedReply:""}; }
}

// ─── DNC Scrub ────────────────────────────────────────────────────────────────
// Litigator Scrub API — replace LITIGATOR_KEY with your actual key from litigatorscrub.com
const LITIGATOR_KEY = process.env.REACT_APP_LITIGATOR_KEY || "";

async function scrubPhone(phone) {
  // Litigator Scrub API endpoint
  // Docs: https://litigatorscrub.com/api
  if (!LITIGATOR_KEY) return { status:"no_key", safe:true };
  try {
    const clean = phone.replace(/\D/g,"");
    const res = await fetch(`https://api.litigatorscrub.com/v1/scrub?phone=${clean}&api_key=${LITIGATOR_KEY}`);
    const data = await res.json();
    // Returns: { dnc: bool, litigator: bool, wireless: bool }
    if (data.litigator) return { status:"litigator", safe:false };
    if (data.dnc)       return { status:"dnc",        safe:false };
    return { status:"clean", safe:true };
  } catch(e) {
    console.error("Litigator Scrub error:", e);
    return { status:"error", safe:true }; // fail open — don't block on API error
  }
}

async function scrubBatch(contacts, onProgress) {
  const results = [...contacts];
  for (let i = 0; i < results.length; i++) {
    if (!results[i].phone) { results[i].dncStatus = "no_phone"; continue; }
    const { status, safe } = await scrubPhone(results[i].phone);
    results[i].dncStatus = status;
    results[i].status = safe ? "pending" : "dnc_blocked";
    onProgress(Math.round(((i+1)/results.length)*100), results[i]);
    await new Promise(r => setTimeout(r, 150)); // rate limit
  }
  return results;
}

// ─── Drip helpers ─────────────────────────────────────────────────────────────
function scheduleDrip(contact) {
  const date90 = new Date();
  date90.setDate(date90.getDate() + 90);
  return {
    ...contact,
    drip: "scheduled",
    dripScheduledDate: date90.toISOString(),
    dripMessage: `Hi ${contact.name.split(" ")[0]}, just checking back — still open to a cash offer on ${contact.address.split(",")[0]}? No repairs, fast close. Reply YES if interested.`,
  };
}

function getDripsDue(contacts) {
  const now = new Date();
  return contacts.filter(c =>
    c.drip === "scheduled" &&
    c.dripScheduledDate &&
    new Date(c.dripScheduledDate) <= now
  );
}

function daysUntilDrip(dateStr) {
  if (!dateStr) return null;
  const diff = new Date(dateStr) - new Date();
  return Math.max(0, Math.round(diff / (1000 * 60 * 60 * 24)));
}

// ─── Status Badge ─────────────────────────────────────────────────────────────
const StatusBadge = ({ status, dncStatus }) => {
  if (dncStatus === "litigator") return <span style={{fontSize:10,fontFamily:"'DM Mono',monospace",fontWeight:700,background:"#F8717122",color:"#F87171",borderRadius:4,padding:"2px 8px"}}>⚖ LITIGATOR</span>;
  if (dncStatus === "dnc")       return <span style={{fontSize:10,fontFamily:"'DM Mono',monospace",fontWeight:700,background:"#FB923C22",color:"#FB923C",borderRadius:4,padding:"2px 8px"}}>🚫 DNC</span>;
  const map = {
    pending:        {label:"Pending",       color:"#475569", bg:"#1E293B"},
    sent:           {label:"Sent",          color:"#38BDF8", bg:"#38BDF811"},
    replied:        {label:"Replied",       color:"#FB923C", bg:"#FB923C11"},
    motivated:      {label:"Motivated 🔥",  color:"#F87171", bg:"#F8717111"},
    info:           {label:"Wants Info",    color:"#C084FC", bg:"#C084FC11"},
    not_interested: {label:"Not Interested",color:"#475569", bg:"#1E293B"},
    opted_out:      {label:"Opted Out",     color:"#334155", bg:"#1E293B"},
    dnc_blocked:    {label:"DNC Blocked",   color:"#FB923C", bg:"#FB923C11"},
  };
  const s = map[status]||map.pending;
  return <span style={{fontSize:10,fontFamily:"'DM Mono',monospace",fontWeight:600,background:s.bg,color:s.color,borderRadius:4,padding:"2px 8px"}}>{s.label}</span>;
};

// ─── Main Component ───────────────────────────────────────────────────────────
export default function OutboundSMS({ currentUser, onGoToCRM }) {
  const [contacts, setContacts]         = useState([]);
  const [template, setTemplate]         = useState("Hi {name}, I came across your property at {address} — would you consider a fast cash offer? No repairs needed. Reply YES to learn more.");
  const [batchSize, setBatchSize]       = useState(50);
  const [batchDelay, setBatchDelay]     = useState(60);
  const [sending, setSending]           = useState(false);
  const [sendProgress, setSendProgress] = useState(0);
  const [aiPersonalize, setAiPersonalize] = useState(true);
  const [activeTab, setActiveTab]       = useState("contacts");
  const [scrubbing, setScrubbing]       = useState(false);
  const [scrubProgress, setScrubProgress] = useState(0);
  const [platform, setPlatform]         = useState("mock");
  const [litigatorKey, setLitigatorKey] = useState(localStorage.getItem("litigatorKey")||"");
  const [fromNumber, setFromNumber]     = useState("");
  const [selectedContact, setSelectedContact] = useState(null);
  const [replyInput, setReplyInput]     = useState("");
  const [classifying, setClassifying]   = useState(false);
  const [isDragging, setIsDragging]     = useState(false);
  const fileRef   = useRef();
  const sendingRef = useRef(false);

  // Stats
  const stats = {
    total:       contacts.length,
    clean:       contacts.filter(c=>c.dncStatus==="clean").length,
    blocked:     contacts.filter(c=>c.status==="dnc_blocked").length,
    litigators:  contacts.filter(c=>c.dncStatus==="litigator").length,
    sent:        contacts.filter(c=>["sent","replied","motivated","info","not_interested","opted_out"].includes(c.status)).length,
    replied:     contacts.filter(c=>c.reply).length,
    motivated:   contacts.filter(c=>c.classification==="motivated").length,
    drip:        contacts.filter(c=>c.drip==="scheduled").length,
  };
  const dripsDue = getDripsDue(contacts);

  // ── File handler ──────────────────────────────────────────────────────────
  const handleFile = (file) => {
    if (!file) return;
    const isXlsx = file.name.endsWith(".xlsx") || file.name.endsWith(".xls");
    const isCsv  = file.name.endsWith(".csv");
    if (!isXlsx && !isCsv) return;
    const reader = new FileReader();
    reader.onload = (e) => {
      let parsed;
      if (isXlsx) {
        const wb = XLSX.read(e.target.result, { type: "array" });
        const ws = wb.Sheets[wb.SheetNames[0]];
        const csv = XLSX.utils.sheet_to_csv(ws);
        parsed = parseCSV(csv);
      } else {
        parsed = parseCSV(e.target.result);
      }
      setContacts(parsed);
      setActiveTab("compliance");
    };
    if (isXlsx) reader.readAsArrayBuffer(file);
    else reader.readAsText(file);
  };

  // ── DNC Scrub ─────────────────────────────────────────────────────────────
  const runScrub = async () => {
    setScrubbing(true);
    setScrubProgress(0);
    if (!litigatorKey) {
      // Demo mode — simulate scrub with 5% blocked
      const updated = contacts.map((c,i) => ({
        ...c,
        dncStatus: i % 20 === 0 ? "dnc" : i % 35 === 0 ? "litigator" : "clean",
        status:    i % 20 === 0 || i % 35 === 0 ? "dnc_blocked" : "pending",
      }));
      for (let i = 0; i <= 100; i += 10) { setScrubProgress(i); await new Promise(r=>setTimeout(r,80)); }
      setContacts(updated);
    } else {
      localStorage.setItem("litigatorKey", litigatorKey);
      const updated = await scrubBatch(contacts, (pct, contact) => {
        setScrubProgress(pct);
        setContacts(prev => prev.map(c => c.id === contact.id ? contact : c));
      });
      setContacts(updated);
    }
    setScrubbing(false);
    setActiveTab("contacts");
  };

  // ── Send batch ────────────────────────────────────────────────────────────
  const sendBatch = async () => {
    const pending = contacts.filter(c => c.status==="pending");
    if (!pending.length) return;
    setSending(true); sendingRef.current = true; setSendProgress(0);
    const toSend = pending.slice(0, batchSize);
    for (let i = 0; i < toSend.length; i++) {
      if (!sendingRef.current) break;
      const contact = toSend[i];
      let msg = template
        .replace(/{name}/gi, contact.name.split(" ")[0]||contact.name)
        .replace(/{address}/gi, contact.address.split(",")[0]||contact.address)
        .replace(/{equity}/gi, contact.equity+"%");
      if (aiPersonalize && ANTHROPIC_KEY) {
        try { msg = await personalizeMessage(contact, template); } catch(e) {}
      }
      if (platform !== "mock") {
        try {
          await fetch("https://wholesaleiq-backend-production.up.railway.app/api/send", {
            method:"POST", headers:{"Content-Type":"application/json"},
            body:JSON.stringify({to:contact.phone, message:msg, from:fromNumber}),
          });
        } catch(e) {}
      } else {
        await new Promise(r=>setTimeout(r,200));
      }
      setContacts(prev=>prev.map(c=>c.id===contact.id?{...c,status:"sent",personalizedMsg:msg}:c));
      setSendProgress(Math.round(((i+1)/toSend.length)*100));
      if (i < toSend.length-1 && batchDelay > 0) await new Promise(r=>setTimeout(r, batchDelay*1000));
    }
    setSending(false); sendingRef.current = false;
  };

  // ── Classify reply & schedule drip ───────────────────────────────────────
  const logReply = async (contactId, replyText) => {
    if (!replyText.trim()) return;
    setClassifying(true);
    const contact = contacts.find(c=>c.id===contactId);
    const result  = await classifyReply(replyText, contact?.name||"Seller");
    setClassifying(false);

    let updated = {
      ...contact,
      reply: replyText,
      status: "replied",
      classification: result.classification,
      aiScore: result.score,
      hot: result.hot,
      aiSummary: result.summary,
      suggestedReply: result.suggestedReply,
    };

    // Schedule 90-day drip for not_interested and info
    if (result.classification === "not_interested" || result.classification === "info") {
      updated = scheduleDrip(updated);
    }

    setContacts(prev => prev.map(c => c.id===contactId ? updated : c));

    // Auto-create pipeline lead if motivated or info
    if ((result.classification==="motivated"||result.classification==="info") && currentUser?.id) {
      const lead = {
        id: Date.now(), user_id: currentUser.id,
        stage: result.classification==="motivated"?"talking":"attempted",
        name: contact?.name||"Unknown", phone: contact?.phone||"",
        address: contact?.address||"", sqft: contact?.sqft||null,
        equity: contact?.equity||0, tax_lien: contact?.taxLien||false,
        vacant: contact?.vacant||false, score: result.score, hot: result.hot,
        notes: `Outbound SMS campaign reply. ${result.summary}`,
        arv:null, comps:[], rehab_tier:null, condition_report:null,
        sms_history:[
          {from:"out", text:contact?.personalizedMsg||template, time:"Campaign"},
          {from:"in",  text:replyText, time:"Just now"},
        ],
        follow_ups:[], last_contact:"Just now",
      };
      await supabase.from("leads").upsert(lead);
    }
    setReplyInput(""); setSelectedContact(null);
  };

  // ── Send drip now ─────────────────────────────────────────────────────────
  const sendDrip = async (contactId) => {
    const contact = contacts.find(c=>c.id===contactId);
    if (!contact?.dripMessage) return;
    if (platform !== "mock") {
      try {
        await fetch("https://wholesaleiq-backend-production.up.railway.app/api/send", {
          method:"POST", headers:{"Content-Type":"application/json"},
          body:JSON.stringify({to:contact.phone, message:contact.dripMessage, from:fromNumber}),
        });
      } catch(e) {}
    }
    setContacts(prev=>prev.map(c=>c.id===contactId?{...c,drip:"sent",status:"sent",personalizedMsg:contact.dripMessage}:c));
  };

  const card = {background:"#080D14",border:"1px solid #1E293B",borderRadius:10,padding:"16px 18px"};
  const motivated = contacts.filter(c=>c.classification==="motivated");

  return (
    <div style={{flex:1,display:"flex",flexDirection:"column",background:"#060A10",fontFamily:"'DM Sans',sans-serif",color:"#F1F5F9",overflow:"hidden"}}>

      {/* Header */}
      <div style={{borderBottom:"1px solid #1E293B",padding:"0 24px",background:"#080D14",display:"flex",alignItems:"center",gap:0,flexShrink:0}}>
        <div style={{padding:"14px 0",marginRight:32}}>
          <div style={{fontSize:9,letterSpacing:3,color:"#334155",fontFamily:"'DM Mono',monospace"}}>WHOLESALEIQ</div>
          <div style={{fontFamily:"'Bebas Neue',display",fontSize:22,letterSpacing:3,color:"#F1F5F9",lineHeight:1.1}}>OUTBOUND SMS</div>
        </div>
        <div style={{display:"flex",gap:0,flex:1}}>
          {[
            {label:"CONTACTS",  value:stats.total,      color:"#CBD5E1"},
            {label:"🚫 BLOCKED", value:stats.blocked,    color:"#FB923C"},
            {label:"SENT",      value:stats.sent,        color:"#38BDF8"},
            {label:"🔥 HOT",    value:stats.motivated,   color:"#F87171"},
            {label:"📅 DRIP",   value:stats.drip,        color:"#C084FC"},
          ].map((s,i)=>(
            <div key={i} style={{padding:"0 18px",borderLeft:"1px solid #1E293B"}}>
              <div style={{fontSize:9,letterSpacing:2,color:"#334155",fontFamily:"'DM Mono',monospace"}}>{s.label}</div>
              <div style={{fontSize:18,fontWeight:800,color:s.color,fontFamily:"'DM Mono',monospace"}}>{s.value}</div>
            </div>
          ))}
        </div>
        <div style={{display:"flex",gap:8}}>
          <label htmlFor="csvFileInput" style={{background:"#38BDF814",border:"1px solid #38BDF833",borderRadius:8,color:"#38BDF8",padding:"7px 14px",cursor:"pointer",fontSize:11,fontFamily:"'DM Mono',monospace",fontWeight:700,display:"inline-flex",alignItems:"center"}}>📂 LOAD CSV</label>
          <input id="csvFileInput" ref={fileRef} type="file" accept=".csv,.xlsx,.xls" style={{display:"none"}} onChange={e=>{handleFile(e.target.files[0]);e.target.value="";}} />
          <button onClick={onGoToCRM} style={{background:"none",border:"1px solid #334155",borderRadius:8,color:"#64748B",padding:"7px 14px",cursor:"pointer",fontSize:11,fontFamily:"'DM Mono',monospace"}}>⊞ Pipeline</button>
        </div>
      </div>

      {/* Tabs */}
      <div style={{display:"flex",borderBottom:"1px solid #1E293B",background:"#080D14",padding:"0 24px",flexShrink:0}}>
        {[
          ["contacts",   "Contacts"],
          ["compliance", `Compliance & DNC${scrubbing?" (scrubbing...)":stats.blocked>0?` (${stats.blocked} blocked)`:""}`],
          ["compose",    "Compose & Send"],
          ["drip",       `Drip Campaign${dripsDue.length>0?` (${dripsDue.length} due)`:stats.drip>0?` (${stats.drip})`:""}`],
          ["results",    `Replies${stats.motivated>0?` (${stats.motivated} 🔥)`:""}`],
          ["settings",   "Settings"],
        ].map(([id,label])=>(
          <button key={id} onClick={()=>setActiveTab(id)} style={{background:"none",border:"none",padding:"10px 14px",fontSize:11,cursor:"pointer",fontFamily:"'DM Mono',monospace",color:activeTab===id?"#38BDF8":"#475569",borderBottom:activeTab===id?"2px solid #38BDF8":"2px solid transparent",whiteSpace:"nowrap"}}>
            {label}
          </button>
        ))}
      </div>

      <div style={{flex:1,overflowY:"auto",padding:24}}>

        {/* ── CONTACTS TAB ─────────────────────────────────────────────────── */}
        {activeTab==="contacts"&&(
          <div>
            {contacts.length===0?(
              <div onDragOver={e=>{e.preventDefault();setIsDragging(true);}} onDragLeave={()=>setIsDragging(false)}
                onDrop={e=>{e.preventDefault();setIsDragging(false);handleFile(e.dataTransfer.files[0]);}}
                
                style={{border:`2px dashed ${isDragging?"#38BDF8":"#334155"}`,borderRadius:12,padding:"60px 24px",textAlign:"center",cursor:"pointer",background:isDragging?"#38BDF808":"transparent"}}>
                <div style={{fontSize:40,marginBottom:12}}>📱</div>
                <div style={{fontSize:16,fontWeight:700,color:"#CBD5E1",marginBottom:8}}>Load your BatchLeads CSV</div>
                <div style={{fontSize:13,color:"#475569",marginBottom:6}}>Drop file here or click to browse</div>
                <div style={{fontSize:12,color:"#38BDF8",marginBottom:16}}>→ After loading, run DNC scrub before sending</div>
                <div style={{display:"flex",gap:8,justifyContent:"center",flexWrap:"wrap"}}>
                  {["Owner Name","Phone","Property Address","Equity %","Tax Lien","Vacant"].map(f=>(
                    <span key={f} style={{fontSize:10,fontFamily:"'DM Mono',monospace",background:"#1E293B",border:"1px solid #334155",color:"#475569",borderRadius:4,padding:"2px 8px"}}>{f}</span>
                  ))}
                </div>
              </div>
            ):(
              <div>
                <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:16}}>
                  <div>
                    <div style={{fontSize:14,color:"#CBD5E1",fontWeight:700}}>{contacts.length} contacts loaded</div>
                    <div style={{fontSize:12,color:"#475569",marginTop:2}}>
                      {stats.clean>0&&<span style={{color:"#4ADE80",marginRight:12}}>✓ {stats.clean} clean</span>}
                      {stats.blocked>0&&<span style={{color:"#FB923C",marginRight:12}}>🚫 {stats.blocked} blocked</span>}
                      {stats.litigators>0&&<span style={{color:"#F87171"}}>⚖ {stats.litigators} litigators</span>}
                      {contacts[0]?.dncStatus==="unchecked"&&<span style={{color:"#FB923C"}}>⚠ DNC scrub not run yet</span>}
                    </div>
                  </div>
                  <div style={{display:"flex",gap:8}}>
                    <button onClick={()=>setContacts([])} style={{background:"none",border:"1px solid #334155",borderRadius:6,color:"#64748B",padding:"6px 12px",cursor:"pointer",fontSize:11}}>Clear</button>
                    {contacts[0]?.dncStatus==="unchecked"&&(
                      <button onClick={()=>setActiveTab("compliance")} style={{background:"#FB923C22",border:"1px solid #FB923C44",borderRadius:6,color:"#FB923C",padding:"6px 14px",cursor:"pointer",fontSize:11,fontFamily:"'DM Mono',monospace",fontWeight:700}}>⚠ RUN DNC SCRUB FIRST</button>
                    )}
                    <button onClick={()=>setActiveTab("compose")} style={{background:"linear-gradient(135deg,#38BDF8,#0284C7)",border:"none",borderRadius:6,color:"#080D14",padding:"6px 16px",cursor:"pointer",fontSize:12,fontWeight:800,fontFamily:"'DM Mono',monospace"}}>COMPOSE →</button>
                  </div>
                </div>
                <div style={{background:"#090E16",border:"1px solid #1E293B",borderRadius:10,overflow:"hidden"}}>
                  <div style={{display:"grid",gridTemplateColumns:"2fr 1.5fr 1fr 1fr 1fr 1fr 1fr",padding:"10px 16px",background:"#0F1923",gap:8}}>
                    {["Name","Address","Equity","Tax Lien","Phone","DNC","Status"].map(h=>(
                      <div key={h} style={{fontSize:9,letterSpacing:2,color:"#475569",fontFamily:"'DM Mono',monospace"}}>{h}</div>
                    ))}
                  </div>
                  {contacts.slice(0,150).map((c)=>(
                    <div key={c.id} style={{display:"grid",gridTemplateColumns:"2fr 1.5fr 1fr 1fr 1fr 1fr 1fr",padding:"9px 16px",gap:8,borderTop:"1px solid #1E293B",
                      background:c.dncStatus==="litigator"?"#F8717108":c.dncStatus==="dnc"?"#FB923C08":c.classification==="motivated"?"#F8717106":"transparent",
                      opacity:c.status==="dnc_blocked"?0.5:1}}>
                      <div style={{fontSize:12,color:"#F1F5F9",fontWeight:600,overflow:"hidden",textOverflow:"ellipsis",whiteSpace:"nowrap"}}>{c.name}</div>
                      <div style={{fontSize:11,color:"#64748B",overflow:"hidden",textOverflow:"ellipsis",whiteSpace:"nowrap"}}>{c.address.split(",")[0]}</div>
                      <div style={{fontSize:12,color:"#38BDF8",fontFamily:"'DM Mono',monospace"}}>{c.equity}%</div>
                      <div style={{fontSize:12,color:c.taxLien?"#F87171":"#475569"}}>{c.taxLien?"YES":"NO"}</div>
                      <div style={{fontSize:11,color:"#64748B",fontFamily:"'DM Mono',monospace",overflow:"hidden",textOverflow:"ellipsis",whiteSpace:"nowrap"}}>{c.phone}</div>
                      <div style={{fontSize:10}}>
                        {c.dncStatus==="clean"&&<span style={{color:"#4ADE80",fontFamily:"'DM Mono',monospace"}}>✓</span>}
                        {c.dncStatus==="dnc"&&<span style={{color:"#FB923C",fontFamily:"'DM Mono',monospace"}}>🚫</span>}
                        {c.dncStatus==="litigator"&&<span style={{color:"#F87171",fontFamily:"'DM Mono',monospace"}}>⚖</span>}
                        {c.dncStatus==="unchecked"&&<span style={{color:"#334155",fontFamily:"'DM Mono',monospace"}}>?</span>}
                      </div>
                      <StatusBadge status={c.status} dncStatus={c.dncStatus}/>
                    </div>
                  ))}
                  {contacts.length>150&&<div style={{padding:"12px 16px",fontSize:12,color:"#475569",borderTop:"1px solid #1E293B"}}>...and {contacts.length-150} more</div>}
                </div>
              </div>
            )}
          </div>
        )}

        {/* ── COMPLIANCE & DNC TAB ─────────────────────────────────────────── */}
        {activeTab==="compliance"&&(
          <div style={{maxWidth:700}}>
            {/* Warning banner */}
            <div style={{background:"#FB923C14",border:"1px solid #FB923C44",borderRadius:10,padding:"14px 18px",marginBottom:20,display:"flex",gap:12,alignItems:"flex-start"}}>
              <div style={{fontSize:20,flexShrink:0}}>⚠</div>
              <div>
                <div style={{fontSize:13,fontWeight:700,color:"#FB923C",marginBottom:4}}>Federal DNC Compliance Required</div>
                <div style={{fontSize:12,color:"#94A3B8",lineHeight:1.7}}>
                  You are legally required to scrub against the National DNC Registry before texting sellers. Violations can result in fines up to <strong style={{color:"#F87171"}}>$51,744 per message</strong>. We recommend Litigator Scrub which checks federal DNC, state DNC lists, and flags known serial litigants who sue wholesalers.
                </div>
              </div>
            </div>

            {/* Scrub results if done */}
            {stats.blocked>0&&(
              <div style={{...card,marginBottom:20,border:"1px solid #4ADE8033"}}>
                <div style={{fontSize:9,letterSpacing:3,color:"#4ADE80",fontFamily:"'DM Mono',monospace",marginBottom:12}}>SCRUB RESULTS</div>
                <div style={{display:"grid",gridTemplateColumns:"1fr 1fr 1fr 1fr",gap:12}}>
                  {[
                    {label:"Total Checked", value:contacts.filter(c=>c.dncStatus!=="unchecked").length, color:"#CBD5E1"},
                    {label:"Clean",         value:stats.clean,       color:"#4ADE80"},
                    {label:"DNC Blocked",   value:contacts.filter(c=>c.dncStatus==="dnc").length, color:"#FB923C"},
                    {label:"Litigators",    value:stats.litigators,  color:"#F87171"},
                  ].map((s,i)=>(
                    <div key={i} style={{background:"#090E16",borderRadius:8,padding:"12px 14px"}}>
                      <div style={{fontSize:9,color:"#475569",fontFamily:"'DM Mono',monospace",marginBottom:4}}>{s.label}</div>
                      <div style={{fontSize:20,fontWeight:800,color:s.color,fontFamily:"'DM Mono',monospace"}}>{s.value}</div>
                    </div>
                  ))}
                </div>
                <div style={{marginTop:12,fontSize:12,color:"#64748B"}}>{stats.blocked} contacts removed from your send list. Only clean contacts will receive messages.</div>
              </div>
            )}

            {/* Litigator Scrub setup */}
            <div style={{...card,marginBottom:16}}>
              <div style={{fontSize:9,letterSpacing:3,color:"#475569",fontFamily:"'DM Mono',monospace",marginBottom:14}}>LITIGATOR SCRUB API KEY</div>
              <div style={{fontSize:12,color:"#64748B",marginBottom:12,lineHeight:1.6}}>
                Get your API key at <span style={{color:"#38BDF8"}}>litigatorscrub.com</span> — covers Federal DNC, State DNC lists, and known serial litigants. ~$0.02–0.05 per number scrubbed.
              </div>
              <input
                value={litigatorKey}
                onChange={e=>setLitigatorKey(e.target.value)}
                placeholder="Enter your Litigator Scrub API key..."
                style={{width:"100%",boxSizing:"border-box",background:"#090E16",border:"1px solid #1E293B",borderRadius:8,padding:"10px 12px",color:"#F1F5F9",fontSize:13,outline:"none",marginBottom:12}}
              />
              {!litigatorKey&&(
                <div style={{background:"#38BDF811",border:"1px solid #38BDF822",borderRadius:8,padding:"10px 14px",marginBottom:12,fontSize:12,color:"#38BDF8"}}>
                  No API key? We'll run a <strong>demo scrub</strong> that simulates the results (~5% blocked). Add your real key for actual DNC compliance.
                </div>
              )}

              {contacts.length===0?(
                <div style={{fontSize:12,color:"#475569"}}>Load a CSV first before running scrub.</div>
              ):(
                <div>
                  {scrubbing?(
                    <div style={{marginBottom:12}}>
                      <div style={{display:"flex",justifyContent:"space-between",marginBottom:6}}>
                        <div style={{fontSize:12,color:"#38BDF8"}}>Scrubbing {contacts.length} numbers...</div>
                        <div style={{fontSize:12,color:"#38BDF8",fontFamily:"'DM Mono',monospace"}}>{scrubProgress}%</div>
                      </div>
                      <div style={{height:6,background:"#1E293B",borderRadius:3,overflow:"hidden"}}>
                        <div style={{width:`${scrubProgress}%`,height:"100%",background:"linear-gradient(90deg,#38BDF8,#0284C7)",borderRadius:3,transition:"width .3s"}}/>
                      </div>
                    </div>
                  ):(
                    <button onClick={runScrub} style={{background:"linear-gradient(135deg,#38BDF8,#0284C7)",border:"none",borderRadius:8,color:"#080D14",padding:"12px 24px",cursor:"pointer",fontSize:13,fontWeight:800,fontFamily:"'DM Mono',monospace"}}>
                      🛡 SCRUB {contacts.length} NUMBERS NOW
                    </button>
                  )}
                </div>
              )}
            </div>

            {/* Compliance checklist */}
            <div style={{...card}}>
              <div style={{fontSize:9,letterSpacing:3,color:"#475569",fontFamily:"'DM Mono',monospace",marginBottom:14}}>COMPLIANCE CHECKLIST</div>
              {[
                {done:stats.clean>0||stats.blocked>0, label:"DNC scrub completed", desc:"Federal + State DNC + Litigator check"},
                {done:contacts.filter(c=>c.status==="dnc_blocked").length===contacts.filter(c=>c.dncStatus==="dnc"||c.dncStatus==="litigator").length, label:"Blocked numbers removed from send list", desc:"DNC/litigator contacts won't receive messages"},
                {done:true, label:"90-day drip for non-responders", desc:"Auto-scheduled for not interested & wants info"},
                {done:true, label:"Opt-out handling", desc:"STOP/Unsubscribe replies marked opted out, never re-contacted"},
                {done:false, label:"TCPA consent documentation", desc:"Keep records of how you obtained contact info — consult an attorney"},
              ].map((item,i)=>(
                <div key={i} style={{display:"flex",gap:12,padding:"10px 0",borderBottom:i<4?"1px solid #1E293B":"none"}}>
                  <div style={{width:20,height:20,borderRadius:"50%",background:item.done?"#4ADE8022":"#1E293B",border:`1px solid ${item.done?"#4ADE80":"#334155"}`,display:"flex",alignItems:"center",justifyContent:"center",flexShrink:0,marginTop:1}}>
                    <span style={{fontSize:11,color:item.done?"#4ADE80":"#475569"}}>{item.done?"✓":"○"}</span>
                  </div>
                  <div>
                    <div style={{fontSize:13,color:item.done?"#CBD5E1":"#64748B",fontWeight:item.done?600:400}}>{item.label}</div>
                    <div style={{fontSize:11,color:"#475569",marginTop:2}}>{item.desc}</div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* ── COMPOSE & SEND TAB ───────────────────────────────────────────── */}
        {activeTab==="compose"&&(
          <div style={{display:"grid",gridTemplateColumns:"1.3fr 1fr",gap:20}}>
            <div>
              {contacts[0]?.dncStatus==="unchecked"&&(
                <div style={{background:"#F8717114",border:"1px solid #F8717133",borderRadius:10,padding:"12px 16px",marginBottom:16,display:"flex",gap:10,alignItems:"center"}}>
                  <span style={{fontSize:16}}>⚠</span>
                  <div style={{fontSize:12,color:"#F87171"}}>DNC scrub not completed. <button onClick={()=>setActiveTab("compliance")} style={{background:"none",border:"none",color:"#38BDF8",cursor:"pointer",fontSize:12,padding:0,textDecoration:"underline"}}>Run it now</button> before sending.</div>
                </div>
              )}
              <div style={{...card,marginBottom:16}}>
                <div style={{fontSize:9,letterSpacing:3,color:"#475569",fontFamily:"'DM Mono',monospace",marginBottom:12}}>MESSAGE TEMPLATE</div>
                <textarea value={template} onChange={e=>setTemplate(e.target.value)} rows={5}
                  style={{width:"100%",boxSizing:"border-box",background:"#090E16",border:"1px solid #1E293B",borderRadius:8,padding:"12px 14px",color:"#F1F5F9",fontSize:13,fontFamily:"'DM Sans',sans-serif",outline:"none",resize:"vertical",lineHeight:1.6}}/>
                <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginTop:8}}>
                  <div style={{fontSize:11,color:"#475569"}}>Use <span style={{color:"#38BDF8",fontFamily:"'DM Mono',monospace"}}>{"{name}"}</span> <span style={{color:"#38BDF8",fontFamily:"'DM Mono',monospace"}}>{"{address}"}</span> <span style={{color:"#38BDF8",fontFamily:"'DM Mono',monospace"}}>{"{equity}"}</span></div>
                  <div style={{fontSize:11,color:template.length>160?"#F87171":"#475569",fontFamily:"'DM Mono',monospace"}}>{template.length}/160</div>
                </div>
                <div style={{marginTop:12,display:"flex",alignItems:"center",gap:10}}>
                  <input type="checkbox" checked={aiPersonalize} onChange={e=>setAiPersonalize(e.target.checked)} style={{width:16,height:16,accentColor:"#38BDF8"}}/>
                  <div>
                    <div style={{fontSize:13,color:"#CBD5E1"}}>AI personalize each message</div>
                    <div style={{fontSize:11,color:"#475569"}}>Claude writes unique messages using seller's name, address, distress signals</div>
                  </div>
                </div>
              </div>
              <div style={{...card,marginBottom:16}}>
                <div style={{fontSize:9,letterSpacing:3,color:"#475569",fontFamily:"'DM Mono',monospace",marginBottom:14}}>BATCH SETTINGS</div>
                <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:14,marginBottom:14}}>
                  <div>
                    <div style={{fontSize:11,color:"#64748B",marginBottom:6}}>Batch size</div>
                    <input type="number" value={batchSize} onChange={e=>setBatchSize(parseInt(e.target.value)||50)} min={1} max={500}
                      style={{width:"100%",background:"#090E16",border:"1px solid #1E293B",borderRadius:8,padding:"10px 12px",color:"#38BDF8",fontSize:16,fontFamily:"'DM Mono',monospace",outline:"none",boxSizing:"border-box"}}/>
                  </div>
                  <div>
                    <div style={{fontSize:11,color:"#64748B",marginBottom:6}}>Delay between messages (sec)</div>
                    <input type="number" value={batchDelay} onChange={e=>setBatchDelay(parseInt(e.target.value)||30)} min={0} max={300}
                      style={{width:"100%",background:"#090E16",border:"1px solid #1E293B",borderRadius:8,padding:"10px 12px",color:"#38BDF8",fontSize:16,fontFamily:"'DM Mono',monospace",outline:"none",boxSizing:"border-box"}}/>
                  </div>
                </div>
                <div style={{background:"#090E16",border:"1px solid #38BDF822",borderRadius:8,padding:"10px 14px",fontSize:12,color:"#64748B"}}>
                  ⏱ ~{Math.round((batchSize*batchDelay)/60)} min for {batchSize} messages at {batchDelay}s delay
                </div>
              </div>
              <div style={{...card}}>
                <div style={{fontSize:9,letterSpacing:3,color:"#475569",fontFamily:"'DM Mono',monospace",marginBottom:12}}>
                  {contacts.filter(c=>c.status==="pending").length} CLEAN CONTACTS READY
                </div>
                {sending&&(
                  <div style={{marginBottom:14}}>
                    <div style={{display:"flex",justifyContent:"space-between",marginBottom:6}}>
                      <div style={{fontSize:12,color:"#38BDF8"}}>Sending...</div>
                      <div style={{fontSize:12,color:"#38BDF8",fontFamily:"'DM Mono',monospace"}}>{sendProgress}%</div>
                    </div>
                    <div style={{height:6,background:"#1E293B",borderRadius:3,overflow:"hidden"}}>
                      <div style={{width:`${sendProgress}%`,height:"100%",background:"linear-gradient(90deg,#38BDF8,#0284C7)",borderRadius:3,transition:"width .3s"}}/>
                    </div>
                  </div>
                )}
                <div style={{display:"flex",gap:10}}>
                  <button onClick={sendBatch} disabled={sending||contacts.filter(c=>c.status==="pending").length===0}
                    style={{flex:1,background:sending?"#1E293B":"linear-gradient(135deg,#38BDF8,#0284C7)",border:"none",borderRadius:8,color:sending?"#475569":"#080D14",padding:"14px",fontSize:13,fontWeight:800,cursor:sending?"not-allowed":"pointer",fontFamily:"'DM Mono',monospace",letterSpacing:1}}>
                    {sending?`SENDING ${sendProgress}%`:`▶ SEND BATCH OF ${Math.min(batchSize,contacts.filter(c=>c.status==="pending").length)}`}
                  </button>
                  {sending&&<button onClick={()=>{sendingRef.current=false;setSending(false);}} style={{background:"#F8717122",border:"1px solid #F87171",borderRadius:8,color:"#F87171",padding:"14px 16px",cursor:"pointer",fontSize:12,fontFamily:"'DM Mono',monospace"}}>STOP</button>}
                </div>
                {platform==="mock"&&<div style={{marginTop:8,fontSize:11,color:"#FB923C"}}>⚠ Mock mode — configure platform in Settings to send real SMS</div>}
              </div>
            </div>
            {/* Preview */}
            <div style={{...card}}>
              <div style={{fontSize:9,letterSpacing:3,color:"#475569",fontFamily:"'DM Mono',monospace",marginBottom:14}}>MESSAGE PREVIEW</div>
              {contacts.filter(c=>c.status==="pending").slice(0,4).map((c,i)=>{
                const preview=template.replace(/{name}/gi,c.name.split(" ")[0]||c.name).replace(/{address}/gi,c.address.split(",")[0]||c.address).replace(/{equity}/gi,c.equity+"%");
                return(
                  <div key={i} style={{marginBottom:16}}>
                    <div style={{fontSize:11,color:"#475569",marginBottom:5,fontFamily:"'DM Mono',monospace"}}>{c.name} · {c.phone}</div>
                    <div style={{background:"#0F3460",border:"1px solid #38BDF833",borderRadius:"14px 4px 14px 14px",padding:"10px 14px",fontSize:13,color:"#CBD5E1",lineHeight:1.55}}>{preview}</div>
                    <div style={{fontSize:10,color:preview.length>160?"#F87171":"#334155",marginTop:4}}>{preview.length} chars</div>
                  </div>
                );
              })}
              {contacts.filter(c=>c.status==="pending").length===0&&<div style={{textAlign:"center",padding:"40px 0",color:"#334155",fontSize:13}}>Load CSV & run DNC scrub first</div>}
            </div>
          </div>
        )}

        {/* ── DRIP CAMPAIGN TAB ────────────────────────────────────────────── */}
        {activeTab==="drip"&&(
          <div>
            {/* Due now */}
            {dripsDue.length>0&&(
              <div style={{...card,marginBottom:20,border:"1px solid #C084FC33"}}>
                <div style={{fontSize:9,letterSpacing:3,color:"#C084FC",fontFamily:"'DM Mono',monospace",marginBottom:14}}>📅 DUE NOW — {dripsDue.length} DRIP MESSAGES READY TO SEND</div>
                {dripsDue.map(c=>(
                  <div key={c.id} style={{padding:"12px 0",borderBottom:"1px solid #1E293B"}}>
                    <div style={{display:"flex",justifyContent:"space-between",alignItems:"flex-start",marginBottom:8}}>
                      <div>
                        <div style={{fontSize:13,fontWeight:700,color:"#F1F5F9"}}>{c.name}</div>
                        <div style={{fontSize:11,color:"#475569"}}>{c.address.split(",")[0]} · {c.phone}</div>
                        <div style={{fontSize:11,color:"#64748B",marginTop:2,fontStyle:"italic"}}>Original reply: "{c.reply}"</div>
                      </div>
                      <button onClick={()=>sendDrip(c.id)} style={{background:"#C084FC22",border:"1px solid #C084FC44",borderRadius:8,color:"#C084FC",padding:"8px 16px",cursor:"pointer",fontSize:11,fontFamily:"'DM Mono',monospace",fontWeight:700,whiteSpace:"nowrap",flexShrink:0}}>
                        SEND NOW
                      </button>
                    </div>
                    <div style={{background:"#0F3460",border:"1px solid #38BDF822",borderRadius:8,padding:"10px 14px",fontSize:12,color:"#CBD5E1"}}>
                      {c.dripMessage}
                    </div>
                  </div>
                ))}
              </div>
            )}

            {/* All scheduled */}
            <div style={{...card}}>
              <div style={{fontSize:9,letterSpacing:3,color:"#475569",fontFamily:"'DM Mono',monospace",marginBottom:14}}>ALL DRIP CAMPAIGNS — {stats.drip} SCHEDULED</div>
              {contacts.filter(c=>c.drip).length===0?(
                <div style={{textAlign:"center",padding:"30px 0",color:"#334155",fontSize:13}}>
                  <div style={{fontSize:28,marginBottom:8}}>📅</div>
                  Drip campaigns auto-schedule when sellers reply "not interested" or "wants info"
                </div>
              ):(
                contacts.filter(c=>c.drip).map(c=>{
                  const days = daysUntilDrip(c.dripScheduledDate);
                  const isDue = days === 0;
                  return(
                    <div key={c.id} style={{padding:"12px 0",borderBottom:"1px solid #1E293B",display:"flex",justifyContent:"space-between",alignItems:"center"}}>
                      <div style={{flex:1}}>
                        <div style={{display:"flex",alignItems:"center",gap:8,marginBottom:4}}>
                          <div style={{fontSize:13,fontWeight:700,color:"#F1F5F9"}}>{c.name}</div>
                          {c.drip==="sent"&&<span style={{fontSize:10,color:"#4ADE80",fontFamily:"'DM Mono',monospace"}}>✓ SENT</span>}
                        </div>
                        <div style={{fontSize:11,color:"#475569"}}>{c.address.split(",")[0]}</div>
                        <div style={{fontSize:11,color:"#64748B",marginTop:2,fontStyle:"italic"}}>"{c.reply}"</div>
                        <div style={{fontSize:11,color:"#C084FC",marginTop:4,fontFamily:"'DM Mono',monospace"}}>
                          {c.drip==="sent"?"Drip sent":isDue?"Due today — ready to send":`Sends in ${days} days`}
                        </div>
                      </div>
                      <div style={{flexShrink:0,marginLeft:16,textAlign:"right"}}>
                        <div style={{fontSize:11,color:"#475569",marginBottom:6}}>{c.dripScheduledDate?new Date(c.dripScheduledDate).toLocaleDateString():""}</div>
                        {c.drip==="scheduled"&&isDue&&(
                          <button onClick={()=>sendDrip(c.id)} style={{background:"#C084FC22",border:"1px solid #C084FC44",borderRadius:6,color:"#C084FC",padding:"6px 12px",cursor:"pointer",fontSize:11,fontFamily:"'DM Mono',monospace",fontWeight:700}}>SEND</button>
                        )}
                      </div>
                    </div>
                  );
                })
              )}
            </div>

            {/* How drips work */}
            <div style={{...card,marginTop:16,border:"1px solid #38BDF822"}}>
              <div style={{fontSize:9,letterSpacing:3,color:"#38BDF8",fontFamily:"'DM Mono',monospace",marginBottom:12}}>HOW DRIP CAMPAIGNS WORK</div>
              <div style={{display:"flex",flexDirection:"column",gap:10}}>
                {[
                  {icon:"💬",label:"Seller replies 'not interested'",desc:"AI classifies → 90-day re-engage scheduled automatically"},
                  {icon:"🤔",label:"Seller replies 'wants info' but doesn't progress",desc:"AI classifies → 90-day re-engage scheduled"},
                  {icon:"📅",label:"Day 90 arrives",desc:"Message appears in Drip tab as Due Now"},
                  {icon:"📱",label:"You click Send Now",desc:"Re-engage message goes out via your SMS platform"},
                  {icon:"🔥",label:"They reply positively",desc:"Log reply → AI classifies as motivated → auto-created in Pipeline"},
                ].map((s,i)=>(
                  <div key={i} style={{display:"flex",gap:12,alignItems:"flex-start"}}>
                    <div style={{fontSize:16,flexShrink:0}}>{s.icon}</div>
                    <div>
                      <div style={{fontSize:13,color:"#CBD5E1",fontWeight:600}}>{s.label}</div>
                      <div style={{fontSize:12,color:"#475569"}}>{s.desc}</div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        )}

        {/* ── RESULTS TAB ──────────────────────────────────────────────────── */}
        {activeTab==="results"&&(
          <div>
            <div style={{display:"grid",gridTemplateColumns:"repeat(4,1fr)",gap:10,marginBottom:20}}>
              {[
                {label:"SENT",          value:stats.sent,                                                          color:"#38BDF8"},
                {label:"REPLIED",       value:stats.replied,                                                       color:"#FB923C"},
                {label:"🔥 MOTIVATED",  value:stats.motivated,                                                     color:"#F87171"},
                {label:"📅 IN DRIP",    value:contacts.filter(c=>c.drip==="scheduled").length,                    color:"#C084FC"},
              ].map((s,i)=>(
                <div key={i} style={{...card}}>
                  <div style={{fontSize:9,letterSpacing:2,color:"#334155",fontFamily:"'DM Mono',monospace",marginBottom:6}}>{s.label}</div>
                  <div style={{fontSize:22,fontWeight:800,color:s.color,fontFamily:"'DM Mono',monospace"}}>{s.value}</div>
                </div>
              ))}
            </div>

            {motivated.length>0&&(
              <div style={{...card,marginBottom:20,border:"1px solid #F8717133"}}>
                <div style={{fontSize:9,letterSpacing:3,color:"#F87171",fontFamily:"'DM Mono',monospace",marginBottom:12}}>🔥 AUTO-CREATED IN PIPELINE</div>
                {motivated.map(c=>(
                  <div key={c.id} style={{display:"flex",alignItems:"center",gap:12,padding:"10px 0",borderBottom:"1px solid #1E293B"}}>
                    <div style={{background:"#F87171",color:"#080D14",fontSize:9,fontWeight:800,fontFamily:"'DM Mono',monospace",borderRadius:4,padding:"2px 6px",flexShrink:0}}>HOT</div>
                    <div style={{flex:1}}>
                      <div style={{fontSize:13,fontWeight:700,color:"#F1F5F9"}}>{c.name}</div>
                      <div style={{fontSize:11,color:"#475569"}}>{c.address}</div>
                      <div style={{fontSize:11,color:"#94A3B8",marginTop:2,fontStyle:"italic"}}>"{c.reply}"</div>
                    </div>
                    <div style={{fontSize:15,fontWeight:800,color:"#38BDF8",fontFamily:"'DM Mono',monospace"}}>{c.aiScore}</div>
                  </div>
                ))}
                <button onClick={onGoToCRM} style={{marginTop:14,width:"100%",background:"#F8717122",border:"1px solid #F8717133",borderRadius:8,color:"#F87171",padding:"10px",cursor:"pointer",fontSize:12,fontFamily:"'DM Mono',monospace",fontWeight:700}}>VIEW IN PIPELINE →</button>
              </div>
            )}

            <div style={{...card}}>
              <div style={{fontSize:9,letterSpacing:3,color:"#475569",fontFamily:"'DM Mono',monospace",marginBottom:14}}>ALL SENT — LOG REPLIES</div>
              {contacts.filter(c=>c.status!=="pending"&&c.status!=="dnc_blocked").length===0?(
                <div style={{textAlign:"center",padding:"30px 0",color:"#334155",fontSize:13}}>No messages sent yet</div>
              ):(
                contacts.filter(c=>c.status!=="pending"&&c.status!=="dnc_blocked").map(c=>(
                  <div key={c.id} style={{padding:"12px 0",borderBottom:"1px solid #1E293B",background:c.classification==="motivated"?"#F8717106":"transparent"}}>
                    <div style={{display:"flex",justifyContent:"space-between",alignItems:"flex-start",marginBottom:6}}>
                      <div>
                        <div style={{fontSize:13,fontWeight:700,color:"#F1F5F9"}}>{c.name}</div>
                        <div style={{fontSize:11,color:"#475569"}}>{c.phone} · {c.address.split(",")[0]}</div>
                      </div>
                      <StatusBadge status={c.classification||c.status} dncStatus={c.dncStatus}/>
                    </div>
                    {c.personalizedMsg&&<div style={{background:"#0F3460",borderRadius:8,padding:"8px 12px",fontSize:12,color:"#94A3B8",marginBottom:8}}>Sent: {c.personalizedMsg}</div>}
                    {c.reply?(
                      <div>
                        <div style={{background:"#1E293B",borderRadius:8,padding:"8px 12px",fontSize:12,color:"#CBD5E1",marginBottom:6}}>Reply: "{c.reply}"</div>
                        {c.aiSummary&&<div style={{fontSize:11,color:"#64748B",fontStyle:"italic",marginBottom:4}}>AI: {c.aiSummary}</div>}
                        {c.drip&&<div style={{fontSize:11,color:"#C084FC"}}>📅 90-day re-engage: {c.drip==="sent"?"sent":daysUntilDrip(c.dripScheduledDate)===0?"due today":`in ${daysUntilDrip(c.dripScheduledDate)} days`}</div>}
                        {c.suggestedReply&&<div style={{fontSize:11,color:"#4ADE80",marginTop:4}}>💬 Suggested: "{c.suggestedReply}"</div>}
                      </div>
                    ):(
                      selectedContact?.id===c.id?(
                        <div style={{display:"flex",gap:8,marginTop:8}}>
                          <input value={replyInput} onChange={e=>setReplyInput(e.target.value)}
                            onKeyDown={e=>e.key==="Enter"&&logReply(c.id,replyInput)}
                            placeholder="Paste seller's reply..."
                            style={{flex:1,background:"#1E293B",border:"1px solid #4ADE8033",borderRadius:6,padding:"8px 10px",color:"#F1F5F9",fontSize:12,outline:"none"}}/>
                          <button onClick={()=>logReply(c.id,replyInput)} disabled={classifying}
                            style={{background:"#4ADE8022",border:"1px solid #4ADE8044",borderRadius:6,color:"#4ADE80",padding:"8px 14px",cursor:"pointer",fontSize:11,fontFamily:"'DM Mono',monospace",fontWeight:700,whiteSpace:"nowrap"}}>
                            {classifying?"AI CLASSIFYING...":"LOG REPLY"}
                          </button>
                          <button onClick={()=>setSelectedContact(null)} style={{background:"none",border:"1px solid #334155",borderRadius:6,color:"#64748B",padding:"8px 10px",cursor:"pointer",fontSize:11}}>✕</button>
                        </div>
                      ):(
                        <button onClick={()=>setSelectedContact(c)} style={{marginTop:6,background:"none",border:"1px dashed #334155",borderRadius:6,padding:"6px 14px",color:"#475569",cursor:"pointer",fontSize:11,fontFamily:"'DM Mono',monospace"}}>+ LOG REPLY</button>
                      )
                    )}
                  </div>
                ))
              )}
            </div>
          </div>
        )}

        {/* ── SETTINGS TAB ─────────────────────────────────────────────────── */}
        {activeTab==="settings"&&(
          <div style={{maxWidth:600}}>
            <div style={{...card,marginBottom:16}}>
              <div style={{fontSize:9,letterSpacing:3,color:"#475569",fontFamily:"'DM Mono',monospace",marginBottom:14}}>SMS PLATFORM</div>
              <div style={{display:"flex",gap:10,marginBottom:16}}>
                {[["twilio","Twilio"],["signalwire","SignalWire"],["mock","Mock (Test)"]].map(([id,label])=>(
                  <button key={id} onClick={()=>setPlatform(id)} style={{flex:1,padding:"12px",borderRadius:8,cursor:"pointer",textAlign:"center",background:platform===id?"#38BDF822":"#090E16",border:`1px solid ${platform===id?"#38BDF8":"#1E293B"}`,color:platform===id?"#38BDF8":"#475569",fontSize:13,fontFamily:"'DM Mono',monospace",fontWeight:700}}>
                    {label}
                  </button>
                ))}
              </div>
              {platform!=="mock"&&(
                <div style={{marginBottom:14}}>
                  <div style={{fontSize:11,color:"#64748B",marginBottom:6}}>Your {platform==="twilio"?"Twilio":"SignalWire"} phone number</div>
                  <input value={fromNumber} onChange={e=>setFromNumber(e.target.value)} placeholder="+17135551234"
                    style={{width:"100%",background:"#090E16",border:"1px solid #1E293B",borderRadius:8,padding:"10px 12px",color:"#F1F5F9",fontSize:13,outline:"none",boxSizing:"border-box"}}/>
                </div>
              )}
              <div style={{background:"#090E16",border:"1px solid #38BDF822",borderRadius:8,padding:14}}>
                <div style={{fontSize:11,fontWeight:700,color:"#CBD5E1",marginBottom:8}}>Webhook URL for inbound replies</div>
                <div style={{fontFamily:"'DM Mono',monospace",fontSize:11,color:"#38BDF8",background:"#060A10",padding:"8px 12px",borderRadius:6,wordBreak:"break-all"}}>
                  https://wholesaleiq-backend-production.up.railway.app/webhook/sms
                </div>
                <div style={{fontSize:11,color:"#475569",marginTop:8}}>Paste this into your {platform==="twilio"?"Twilio":"SignalWire"} phone number settings → inbound webhook URL. Replies auto-classify and create pipeline leads.</div>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
